import { useMemo, useState } from "react";
import { ClipboardDocumentIcon, NoSymbolIcon } from "@heroicons/react/24/outline";
import Modal from "components/ui/modals";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import MasterPage, { AddButton } from "components/ui/MasterPage";
import { ActionButton, Badge } from "components/ui/Table";
import { useAllUsers } from "http-hooks/users/useUsers";
import {
  useCompanyApiTokens,
  useCreateCompanyApiToken,
  useRevokeCompanyApiToken,
} from "http-hooks/companyAdmin/useCompanyApiToken";

const initialFormData = {
  name: "",
  userId: null,
  neverExpires: true,
  expiresAt: "",
};

const tokenColumns = [
  { key: "name", header: "Token Name" },
  {
    key: "user",
    header: "User",
    render: (item) => item.user ? `${item.user.name} (${item.user.username})` : "-",
  },
  { key: "tokenPrefix", header: "Token Prefix" },
  {
    key: "expiresAt",
    header: "Expires",
    render: (item) => item.expiresAt ? new Date(item.expiresAt).toLocaleString() : "Never",
  },
  {
    key: "lastUsedAt",
    header: "Last Used",
    render: (item) => item.lastUsedAt ? new Date(item.lastUsedAt).toLocaleString() : "Never",
  },
  {
    key: "status",
    header: "Status",
    render: (item) =>
      item.revokedAt ? <Badge variant="danger">Revoked</Badge> : <Badge variant="success">Active</Badge>,
  },
  { key: "actions", header: "Actions", isActions: true },
];

function CreateTokenForm({ users, onClose, onCreated }) {
  const [formData, setFormData] = useState(initialFormData);
  const { mutateAsync: createToken, isPending } = useCreateCompanyApiToken();

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const data = await createToken({
        name: formData.name,
        userId: formData.userId?.value,
        expiresAt: formData.neverExpires ? null : `${formData.expiresAt}T23:59:59.999Z`,
      });
      onCreated(data.token);
      onClose();
    } catch (error) {
      console.error("API token creation failed:", error);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2 className="text-lg font-semibold text-gray-900">Create API Token</h2>
      <p className="mt-1 text-sm text-gray-500">
        The token inherits the selected user&apos;s permissions and is shown only once.
      </p>
      <div className="mt-5 space-y-4">
        <Input
          label="Token Name *"
          name="name"
          value={formData.name}
          onChange={(event) => setFormData((previous) => ({ ...previous, name: event.target.value }))}
          maxLength={50}
          required
        />
        <Select
          label="API User *"
          value={formData.userId}
          onChange={(userId) => setFormData((previous) => ({ ...previous, userId }))}
          options={users}
          placeholder="Select an active user"
          isClearable
        />
        <Input
          label="Never expires"
          name="neverExpires"
          type="checkbox"
          value={formData.neverExpires}
          onChange={(event) =>
            setFormData((previous) => ({
              ...previous,
              neverExpires: event.target.checked,
              expiresAt: event.target.checked ? "" : previous.expiresAt,
            }))
          }
        />
        {!formData.neverExpires && (
          <Input
            label="Expiry Date *"
            name="expiresAt"
            type="date"
            min={new Date().toISOString().slice(0, 10)}
            value={formData.expiresAt}
            onChange={(event) => setFormData((previous) => ({ ...previous, expiresAt: event.target.value }))}
            required
          />
        )}
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={isPending}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isPending || !formData.userId || (!formData.neverExpires && !formData.expiresAt)}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isPending ? "Creating..." : "Create Token"}
        </button>
      </div>
    </form>
  );
}

export default function CompanyApiTokens() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [visibleToken, setVisibleToken] = useState(null);
  const { data: tokens = [], isLoading } = useCompanyApiTokens();
  const { data: usersResult } = useAllUsers({ page: 1, limit: 10000000, flag: 1 });
  const { mutateAsync: revokeToken, isPending: isRevoking } = useRevokeCompanyApiToken();
  const userOptions = useMemo(
    () =>
      (usersResult?.data || [])
        .filter((user) => user.flag === 1)
        .map((user) => ({ value: user.id, label: `${user.name} (${user.username})` })),
    [usersResult]
  );

  const handleRevoke = async (id) => {
    try {
      await revokeToken(id);
    } catch (error) {
      console.error("API token revocation failed:", error);
    }
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(visibleToken);
    } catch (error) {
      console.error("Token copy failed:", error);
    }
  };

  return (
    <MasterPage
      title="API Tokens"
      description="Tokens act as the selected user and inherit that user's permissions."
      headerActions={<AddButton onClick={() => setIsCreateOpen(true)}>Create API Token</AddButton>}
      columns={tokenColumns}
      data={tokens}
      isLoading={isLoading}
      rowKey="id"
      renderActions={(item) =>
        item.revokedAt ? null : (
          <ActionButton
            variant="danger"
            onClick={() => handleRevoke(item.id)}
            title="Revoke token"
            disabled={isRevoking}
          >
            <NoSymbolIcon className="h-4 w-4" />
          </ActionButton>
        )
      }
      extraModals={
        <>
          {isCreateOpen && (
            <Modal open setOpen={() => setIsCreateOpen(false)} maxWidth="lg">
              <CreateTokenForm
                users={userOptions}
                onClose={() => setIsCreateOpen(false)}
                onCreated={setVisibleToken}
              />
            </Modal>
          )}
          {visibleToken && (
            <Modal open setOpen={() => setVisibleToken(null)} maxWidth="2xl">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Copy your API token now</h2>
                <p className="mt-2 text-sm text-red-600">
                  This is the only time the full token will be shown.
                </p>
                <div className="mt-4 flex rounded-md">
                  <code className="flex-1 overflow-x-auto rounded-l-md bg-gray-100 p-3 text-sm text-gray-900">
                    {visibleToken}
                  </code>
                  <button
                    type="button"
                    onClick={copyToken}
                    className="rounded-r-md bg-indigo-600 px-3 text-white hover:bg-indigo-500"
                    title="Copy API token"
                  >
                    <ClipboardDocumentIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setVisibleToken(null)}
                    className="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-500"
                  >
                    I have copied it
                  </button>
                </div>
              </div>
            </Modal>
          )}
        </>
      }
    />
  );
}
